import React, {useEffect, useRef, useState} from 'react';
import {formatBackendDateTime} from '../../../../../utils/DateUtils';
import PropTypes from 'prop-types';
import {getContactFromConversation} from '../../../../../utils/ComponentUtilFunctions';
import config from '../../../../../config';
import {getUserByEmails} from "../../../../../services/company/api";
import useHotkeyActions from "../../../../../utils/useHotkeyAction";
import {hotkeyFor} from "../../../../../utils/hotkeys";
import ChatMoreInfoModal from "../../../../../components-new/general/ChatMoreInfoModal";
import ProfileImageViewer from "../../../../../components/ProfileImageViewer";
import BlockUserModal from "./BlockUserModal";
import EndConversationModal from "./EndConversationModal";
import TicketModal from "./TicketModal";
import ForwardDialogModal from "./ForwardDialogModal";
import ForwardIcon from "../../../../../assets/icons/ForwardIcon";
import InfoIcon from "../../../../../assets/icons/InfoIcon";
import NotebookPenIcon from "../../../../../assets/icons/NotebookPenIcon";
import PowerIcon from "../../../../../assets/icons/PowerIcon";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import FileSearchCornerIcon from "../../../../../assets/icons/FileSearchCornerIcon";
import Volume2Icon from "../../../../../assets/icons/Volume2Icon";
import VolumeOffIcon from "../../../../../assets/icons/VolumeOffIcon";
import ContactAvatar from "../../../../../components-new/general/ContactAvatar";
import SilverButton from "../../../../../components-new/buttons/SilverButton";
import HeaderIconButton from "../../../../../components-new/buttons/HeaderIconButton";
import Popup from "../../../../../components-new/ui/Popup";
import Input from "../../../../../components-new/form/Input";
import TranslateProperty from "../../../../../components/TranslateProperty";
import DeleteButton from "../../../../../components-new/buttons/actions/DeleteButton";
import DropdownSelect from "../../../../../components-new/form/DropdownSelect";
import Translate from "../../../../../components/Translate";
import MoveUpIcon from "../../../../../assets/icons/MoveUpIcon";
import MoveDownIcon from "../../../../../assets/icons/MoveDownIcon";
import {emitter} from "../../../../../services/emitter";
import Tabs from "../../../../../components-new/ui/Tabs";
import AudioLinesIcon from "../../../../../assets/icons/AudioLinesIcon";
import PaperClipIcon from "../../../../../assets/icons/PaperClipIcon";
import "../chat-css/ConversationHeader.css"
import MinusIcon from "../../../../../assets/icons/MinusIcon";
import PersonOffIcon from "../../../../../assets/icons/PersonOffIcon";
import ChevronLeftIcon from "../../../../../assets/icons/ChevronLeftIcon";

/*
 * The header of a conversation.
 *
 * It was a solid slab of the brand navy with white text on it - the only
 * saturated block on the screen, and the one that had least to say: whose chat
 * this is. It is a white panel now, like the message window under it, and the
 * things in it are told apart by weight rather than by colour.
 */
const headerStyles = {
    /*
     * The strip over the top of the conversation, as the widget draws it: no
     * ground of its own, a blur behind it, and the messages showing through as
     * they scroll under. The look is in conversation-header-bar; what is left
     * here is the shape.
     */
    header: {
        display: 'flex',
        flexDirection: 'row',
        padding: '12px 16px',
        height: '75px',
        boxSizing: 'border-box',
        alignItems: 'center',
    },
    userNameText: {
        margin: 0,
        lineHeight: '18px',
        fontSize: '14px',
        color: 'var(--ds-text-primary)',
        fontWeight: 600,
        maxWidth: '300px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    // e-mail and phone on one muted line instead of stacked rows
    userInfoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '300px',
        overflow: 'hidden',
        color: 'var(--ds-text-tertiary)',
    },
    userInfoText: {
        lineHeight: '14px',
        fontSize: '11px',
        fontWeight: 400,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    userInfoSeparator: {
        fontSize: '11px',
        lineHeight: '14px',
        color: 'var(--ds-neutral-400)',
    },
};

const ConversationHeader = (props) => {
    const {
        conversation, openContactList, readOnly, hasBackButton, fromUnmanagedList,
        filteredMembers, forwardConversation, user, tariff, company, toggleMediaViewDiv, hotkeys,
        showConversationHistory, uniqueTimeline, messageSearch, handleMessageSearch, searchResults, currentSearchIndex,
        handleNextSearch, handlePreviousSearch, messages, clearSelectedConversationFromStore, isConversationHistory
    } = props;

    const [isSoundEnabled, setIsSoundEnabled] = useState(true);
    const [dialogState, setDialogState] = useState({isOpen: false, mode: ''});
    const features = tariff ? tariff.features : [];

    const [members, setMembers] = useState([]);

    const [isInnerModalOpen, setIsInnerModalOpen] = useState(false);
    // which tab the details panel should open on - see openInfoModal
    const [infoModalTab, setInfoModalTab] = useState('chat');
    const [isProfileViewerOpen, setIsProfileViewerOpen] = useState(false);
    const [isBlockUserModalOpen, setIsBlockUserModalOpen] = useState(false);
    const [isEndConversationModalOpen, setIsEndConversationModalOpen] = useState(false);
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);

    const [selectedTimeline, setSelectedTimeline] = useState(null);
    const [activeMediaTab, setActiveMediaTab] = useState('PHOTO');

    const [searchPopupOpen, setSearchPopupOpen] = useState(false);
    const [mediaPopupOpen, setMediaPopupOpen] = useState(false);

    const searchMessageRef = useRef(null);

    useEffect(() => {
        if (conversation.id) {
            const stored = sessionStorage.getItem(`soundEnabled-${conversation.id}`);
            setIsSoundEnabled(stored !== 'false');
        }

    }, [conversation.id]);

    useEffect(() => {

        const handleArrowKeys = (e) => {
            if (!searchPopupOpen) return;

            if (e.key === "ArrowUp") {
                e.preventDefault();
                handleNextSearch();
            }

            if (e.key === "ArrowDown") {
                e.preventDefault();
                handlePreviousSearch();
            }
        }

        document.addEventListener("keydown", handleArrowKeys);

        return () => {
            document.removeEventListener("keydown", handleArrowKeys);
        };
    }, [searchPopupOpen]);

    useEffect(() => {
        if (searchPopupOpen) {
            if (searchMessageRef.current) {
                searchMessageRef.current.focus();
            }
        }
    }, [searchPopupOpen]);

    const contact = getContactFromConversation(conversation);
    const firstName = contact.firstName ? contact.firstName : '';
    const lastName = contact.lastName ? contact.lastName : '';
    const thumbUrl = contact.thumbUrl ? contact.thumbUrl : null;

    /*
     * The combination a button answers to, for the tooltip to show under its
     * name. Empty where the keys are not listened for at all - a history or an
     * internal chat registers no handlers, and a hint there would be a promise
     * the screen does not keep.
     */
    const hotkeysActive = !readOnly && contact.platform !== config.platforms.company;
    const keyFor = (action) => hotkeysActive ? hotkeyFor(hotkeys, action) : '';

    const openDialog = (mode) => {
        setDialogState({isOpen: true, mode});
    };

    const closeDialog = () => {
        setDialogState({isOpen: false, mode: ''});
    };

    const toggleSound = () => {
        const newValue = !isSoundEnabled;
        sessionStorage.setItem(`soundEnabled-${conversation.id}`, newValue);
        setIsSoundEnabled(newValue);
    };

    const toggleSearchPopup = () => {
        setSearchPopupOpen(!searchPopupOpen)
    };
    const toggleMediaPopup = () => {
        setMediaPopupOpen(!mediaPopupOpen)
    };

    const handleForwardClick = () => {
        getUserByEmails(filteredMembers.map(m => m.email)).then(data => {
            const updatedData = data.map(user => ({
                ...user,
                id: user.platformId
            }));
            const sortedData = updatedData.sort((a, b) => a.currentStatus.localeCompare(b.currentStatus));
            setMembers(sortedData);
        });

        openDialog('forward');
    };

    if (hotkeysActive) {
        /*
         * Only what the toolbar is allowed to draw. Forwarding and the black
         * list are sold with the package, and their shortcut used to open the
         * window the package had taken the button away from.
         */
        const actionHandlers = {
            OPEN_END_CHAT_MODAL: () => openEndConversationModal(),
            OPEN_SAVE_INFO_MODAL: () => openTicketModal(),
            OPEN_SEARCH_MODAL: () => toggleSearchPopup(),
            OPEN_FILES_MODAL: () => toggleMediaPopup(),
            OPEN_INFO_MODAL: () => openInfoModal(),
        };

        if (features.includes(config.features.CONVERSATION_FORWARD)) {
            actionHandlers.OPEN_FORWARD_DIALOG = () => handleForwardClick();
        }

        if (features.includes(config.features.BLACK_LIST)) {
            actionHandlers.OPEN_BLOCK_USER_MODAL = () => openBlockUserModal();
        }

        useHotkeyActions(hotkeys, actionHandlers);
    }

    /*
     * The panel has two ways in now. The info button in the toolbar opens it on
     * the conversation, which is what that button has always meant; the visitor's
     * name opens it on the visitor - their e-mail, phone, device and browser are
     * all on that tab, and the name is where anyone looking for them would press.
     */
    const openInfoModal = (tab = 'chat') => {
        setInfoModalTab(tab);
        setIsInnerModalOpen(true);
    }

    const closeInfoModal = () => {
        setIsInnerModalOpen(false);
    }

    const openBlockUserModal = () => {
        setIsBlockUserModalOpen(true);
    }

    const openEndConversationModal = () => {
        setIsEndConversationModalOpen(true);
    }

    const openTicketModal = () => {
        setIsTicketModalOpen(true);
    }

    // same guards the individual buttons carried before, hoisted so the toolbar
    // can drop a whole group (and its divider) when nothing in it is visible
    const showConversationActions = contact.platform !== config.platforms.company;
    /* who may open the chat details - the info button and the name both ask this */
    const canOpenDetails = (!readOnly || fromUnmanagedList)
        && contact.platform !== config.platforms.company;

    const showDestructiveActions = (!readOnly || fromUnmanagedList)
        && contact.platform !== config.platforms.company;

    return (
        <div className="conversation-header-bar" style={{...headerStyles.header}}>
            {hasBackButton && (
                <div className="lg-hidden-up">
                    <HeaderIconButton
                        onClick={openContactList}
                        icon={ <ChevronLeftIcon />}
                    />
                </div>
            )}
            <div className="contact-info"
                 style={{
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     gap: '22px'
                 }}
            >
                <div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px'
                    }}>
                        <div>
                            <div
                                style={{
                                    height: '35px',
                                    width: '35px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    marginRight: '5px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={thumbUrl ? () => setIsProfileViewerOpen(true) : undefined}
                            >
                                <ContactAvatar contact={contact} variant="chatMenu" size={35}/>
                            </div>

                            {isProfileViewerOpen && (
                                <ProfileImageViewer
                                    imageUrl={thumbUrl}
                                    close={() => setIsProfileViewerOpen(false)}
                                />
                            )}
                        </div>

                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            gap: '3px',
                            flexDirection: 'column',
                            minWidth: 0
                        }}>
                            {/* The name is the second way into the details panel,
                                and it is offered exactly where the info button in
                                the toolbar is - not in the history, where a
                                conversation is read rather than worked on, and not
                                in an internal chat, where there is no visitor
                                behind the name to describe. */}
                            <p
                                style={canOpenDetails
                                    ? {...headerStyles.userNameText, cursor: 'pointer'}
                                    : headerStyles.userNameText}
                                onClick={canOpenDetails ? () => openInfoModal('user') : undefined}
                            >
                                {`${firstName} ${lastName}`}
                            </p>
                            {conversation.platform !== config.platformsMap.company &&
                                (contact.visitorEmail || contact.visitorPhoneNumber) &&
                                <div style={headerStyles.userInfoRow}>
                                    {contact.visitorEmail &&
                                        <span style={headerStyles.userInfoText}>{contact.visitorEmail}</span>
                                    }
                                    {contact.visitorEmail && contact.visitorPhoneNumber &&
                                        <span style={headerStyles.userInfoSeparator}>·</span>
                                    }
                                    {contact.visitorPhoneNumber &&
                                        <span style={headerStyles.userInfoText}>{contact.visitorPhoneNumber}</span>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-spacer"/>

            <div className="header-toolbar">
                <div className="header-toolbar__group">
                    {canOpenDetails && ( // conflict on statisticsConvModal (different info - opConv and conv)
                        <HeaderIconButton
                            tooltip="info"
                            hotkey={keyFor('OPEN_INFO_MODAL')}
                            icon={<InfoIcon/>}
                            onClick={() => openInfoModal()}
                        />
                    )}

                    <Popup
                        isOpen={mediaPopupOpen}
                        onOpenChange={setMediaPopupOpen}
                        trigger={
                            <HeaderIconButton
                                tooltip="files"
                                hotkey={keyFor('OPEN_FILES_MODAL')}
                                active={mediaPopupOpen}
                                icon={<FileSearchCornerIcon/>}
                            />
                        }
                        panelAlign={{
                            horizontal: isConversationHistory ? "left" : conversation.platform === config.platformsMap.company ? 'left' : 'center',
                            vertical: ''
                        }}
                        panelStyle={{overflow: 'visible'}}
                        panelInnerStyle={{overflow: 'visible'}}
                    >
                        <div style={{
                            padding: '10px',
                            width: '360px'
                        }}>
                            <Tabs
                                selectedTab={activeMediaTab}
                                tabs={['PHOTO', 'VIDEO', 'AUDIO', 'FILE']}
                                onTabChange={setActiveMediaTab}
                                renderTab={(tab, isActive) => {
                                    const count = messages.filter(m => m.type === tab).length;
                                    return (
                                        <span>
                                         <Translate text={tab}/>
                                            {isActive && ` (${count})`}
                                    </span>
                                    )
                                }}
                                variant={'underline'}
                                style={{justifyContent: 'center'}}
                            />
                            <div className="media-grid">
                                {messages.filter(m => m.type === activeMediaTab).length > 0 ? (
                                    messages
                                        .filter(m => m.type === activeMediaTab)
                                        .map((msg) => (
                                            <div
                                                key={`media_${msg.id}`}
                                                className="media-item"
                                                onClick={() => emitter.emmit('scrollToMessageById', msg.id)}
                                            >
                                                {msg.type === 'PHOTO' &&
                                                    <img src={msg.attachment.url + '&thumb=true'} alt="media"
                                                         draggable={false}
                                                         onDragStart={event => event.preventDefault()}
                                                         loading={'lazy'}/>}
                                                {msg.type === 'VIDEO' && <video src={msg.attachment.url} muted/>}
                                                {msg.type === 'AUDIO' && (
                                                    <div className="file-icon">
                                                        <AudioLinesIcon height={20} width={20}/>
                                                        <span className="file-extension">
                                                            {msg.attachment.name && (
                                                                msg.attachment.name.split('.').pop()
                                                            )}
                                                    </span>
                                                    </div>
                                                )}
                                                {msg.type === 'FILE' && (
                                                    <div className="file-icon">
                                                        <PaperClipIcon height={20} width={20}/>
                                                        <span className="file-extension">
                                                            {msg.attachment.name && (
                                                                msg.attachment.name.split('.').pop()
                                                            )}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                ) : (
                                    <div className="no-files-message">
                                        <Translate text={'noFilesToDisplay'}/>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Popup>

                    <Popup
                        isOpen={searchPopupOpen}
                        onOpenChange={setSearchPopupOpen}
                        trigger={
                            <HeaderIconButton
                                tooltip="search"
                                hotkey={keyFor('OPEN_SEARCH_MODAL')}
                                active={searchPopupOpen}
                                icon={<SearchIcon/>}
                            />
                        }
                        panelAlign={{
                            horizontal: isConversationHistory ? "left" : conversation.platform === config.platformsMap.company ? 'left' : 'center',
                            vertical: ''
                        }}
                        panelStyle={{overflow: 'visible'}}
                        panelInnerStyle={{overflow: 'visible'}}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: '10px',
                                alignItems: 'center',
                            }}
                        >
                            <TranslateProperty properties={[{key: 'placeholder', value: 'searchByText'}]}>
                                <Input
                                    ref={searchMessageRef}
                                    style={{
                                        width: '200px',
                                        height: '40px',
                                    }}
                                    onChange={handleMessageSearch}
                                    value={messageSearch}
                                />
                            </TranslateProperty>

                            {messageSearch && (
                                <DeleteButton
                                    onClick={() => handleMessageSearch({target: {value: ''}})}
                                />
                            )}

                            {searchResults.length > 0 && (
                                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <SilverButton
                                        onClick={handleNextSearch}
                                        styles={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '32px',
                                            height: '32px',
                                            padding: '4px',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <MoveUpIcon stroke={'currentColor'} width={18} height={16}/>
                                    </SilverButton>
                                    <span>{currentSearchIndex + 1}/{searchResults.length}</span>
                                    <SilverButton
                                        onClick={handlePreviousSearch}
                                        styles={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '32px',
                                            height: '32px',
                                            padding: '4px',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <MoveDownIcon stroke={'currentColor'} width={18} height={16}/>
                                    </SilverButton>
                                </div>
                            )}

                            {uniqueTimeline.length > 0 && (
                                <DropdownSelect
                                    selectedValue={uniqueTimeline.find(t => t.date === selectedTimeline)}
                                    onChange={(e) => {
                                        setSelectedTimeline(e.date);
                                        emitter.emmit('scrollToMessageById', e.id)
                                    }}
                                    options={uniqueTimeline}
                                    renderLabel={t => t ? formatBackendDateTime(t.date, undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    }) : <Translate text={'selectTimeline'}/>}
                                    styles={{
                                        width: '140px',
                                    }}
                                    classes="message-search-timeline-dropdown"
                                >
                                    {option => formatBackendDateTime(option.date, undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    })
                                    }
                                </DropdownSelect>
                            )}

                        </div>
                    </Popup>

                </div>

                {showConversationActions && (
                    <React.Fragment>
                        <span className="header-toolbar__divider"/>

                        <div className="header-toolbar__group">
                            {(!readOnly || fromUnmanagedList) && (
                                <HeaderIconButton
                                    tooltip="muteOrUnmute"
                                    icon={isSoundEnabled ? <Volume2Icon/> : <VolumeOffIcon/>}
                                    onClick={toggleSound}
                                />
                            )}

                            {(!readOnly || fromUnmanagedList) && features.includes(config.features.CONVERSATION_FORWARD) && (
                                <HeaderIconButton
                                    tooltip="forward"
                                    hotkey={keyFor('OPEN_FORWARD_DIALOG')}
                                    icon={<ForwardIcon/>}
                                    onClick={() => {
                                        handleForwardClick()
                                    }}
                                />
                            )}

                            <HeaderIconButton
                                tooltip="notes"
                                hotkey={keyFor('OPEN_SAVE_INFO_MODAL')}
                                icon={<NotebookPenIcon strokeWidth={1.5}/>}
                                onClick={openTicketModal}
                            />
                        </div>
                    </React.Fragment>
                )}

                {showDestructiveActions && (
                    <React.Fragment>
                        <span className="header-toolbar__divider"/>

                        <div className="header-toolbar__group">
                            {features.includes(config.features.BLACK_LIST) && (
                                <HeaderIconButton
                                    tooltip="blockUser"
                                    hotkey={keyFor('OPEN_BLOCK_USER_MODAL')}
                                    danger
                                    icon={<PersonOffIcon/>}
                                    onClick={openBlockUserModal}
                                />
                            )}

                            <HeaderIconButton
                                tooltip="endConversation"
                                hotkey={keyFor('OPEN_END_CHAT_MODAL')}
                                danger
                                icon={<PowerIcon strokeWidth={1.5}/>}
                                onClick={openEndConversationModal}
                            />
                        </div>
                    </React.Fragment>
                )}

                {!isConversationHistory && (
                    <React.Fragment>
                        <span className="header-toolbar__divider"/>
                        <HeaderIconButton
                            tooltip="close"
                            icon={<MinusIcon strokeWidth={1.5}/>}
                            onClick={clearSelectedConversationFromStore}
                        />
                    </React.Fragment>
                )}

            </div>

            {
                dialogState.isOpen &&
                <ForwardDialogModal
                    state={dialogState}
                    close={closeDialog}
                    contactList={members}
                    contactClick={forwardConversation}
                    dialogMode={dialogState.mode}
                    user={user}
                    conversation={conversation}
                />
            }

            {
                isInnerModalOpen &&
                <ChatMoreInfoModal
                    isOpen={isInnerModalOpen}
                    onRequestClose={closeInfoModal}
                    company={company}
                    conversation={conversation}
                    initialTab={infoModalTab}
                />
            }

            {
                isBlockUserModalOpen &&
                <BlockUserModal
                    isOpen={isBlockUserModalOpen}
                    cancelAction={() => setIsBlockUserModalOpen(false)}
                    conversation={conversation}
                />
            }

            {
                isEndConversationModalOpen &&
                <EndConversationModal
                    features={features}
                    isOpen={isEndConversationModalOpen}
                    cancelAction={() => setIsEndConversationModalOpen(false)}
                    conversation={conversation}
                    user={user}
                />
            }

            {
                isTicketModalOpen &&
                <TicketModal
                    features={features}
                    isOpen={isTicketModalOpen}
                    cancelAction={() => setIsTicketModalOpen(false)}
                    conversation={conversation}
                    user={user}
                    company={company}
                />
            }

        </div>
    )
        ;
};

ConversationHeader.propTypes = {
    conversation: PropTypes.object.isRequired,
    openContactList: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    hasBackButton: PropTypes.bool,
};

ConversationHeader.defaultProps = {
    readOnly: false,
    hasBackButton: true,
};

export default ConversationHeader;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/ConversationHeader.jsx